import React, { useEffect, useState, useContext } from "react"
import { useLocation } from "react-router-dom"
import { useQuery } from "@apollo/client"
// Context
import { UserContext } from "../../../context/userContext"

// Prime
import { Accordion, AccordionTab } from "primereact/accordion"
import { Dropdown } from "primereact/dropdown"

// Comps
import { LoaderMedium, LoaderSmall } from "../../../components/Loaders"
import Form from "./Form"

// Queries
import { GET_PARTICIPANT_PROFILE, GET_MY_ORGANIZATIONS } from "./queries"

import styles from "../../../styles/Participant-Profile.module.css"
import { LogError } from "../../../helpers/logger"
import { Tooltip } from "primereact/tooltip"

const ProfilePage = () => {
  const location = useLocation()
  const userCtx = useContext(UserContext)
  const [blocks, setBlocks] = useState([])
  const [activeIndex, setActiveIndex] = useState()
  const [loading, setLoading] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [selectedOrganization, setSelectedOrganization] = useState(null)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get("tab")) {
      const newIndex = Number(searchParams.get("tab"))
      setActiveIndex(newIndex)
    }

    setLoading(true)

    refetch()
      .then(({ data }) => {
        let blocks = data.participantProfile.blocks
        const sortedBlocks = [...blocks].sort((a, b) => {
          return a.block_title.localeCompare(b.block_title)
        })
        const renamedBlocks = sortedBlocks.map((block) => {
          let newBlock = {
            ...block,
            block_title: `${block.block_title}`.replace("Participant ", ""),
          }
          if (newBlock.block_name === "participant__consent") {
            newBlock.className = styles.participant_consent
          }
          return newBlock
        })
        setBlocks(renamedBlocks)
      })
      .catch((error) => {
        LogError("Error fetching participant blocks:", error)
      })
      .finally(() => {
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { refetch } = useQuery(GET_PARTICIPANT_PROFILE, {
    fetchPolicy: "network-only",
    variables: {
      participant_id: userCtx.participant_id,
      idBlock: "participant",
    },
    skip: true,
  })

  const getOrgs = useQuery(GET_MY_ORGANIZATIONS, {
    variables: {
      participant_id: userCtx.participant_id,
    },
    context: {
      headers: {},
    },
    skip: !userCtx,
    fetchPolicy: "network-only",
    onCompleted: ({ organizations }) => {
      setOrganizations(organizations) // Set all organizations
      if (organizations.length > 0) {
        // If there are organizations, set the first one as the default
        setSelectedOrganization(organizations[0])
      }
    },
  })

  if (loading) return <LoaderMedium />

  return (
    <div>
      <div className={styles.page_header}>
        <h3>Profile</h3>
        {getOrgs.loading ? (
          <LoaderSmall />
        ) : (
          <div className="flex align-items-center gap-2">
            <Tooltip target="#org-select-tip-icon" />
            <i
              id="org-select-tip-icon"
              style={{ color: "var(--branded-purple)" }}
              className="pi pi-info-circle"
              data-pr-tooltip="Switch between profiles at different organizations"
              data-pr-position="left"
            />
            <Dropdown
              value={selectedOrganization?.id}
              options={organizations}
              panelStyle={{ fontSize: "12px" }}
              onChange={(e) => {
                setSelectedOrganization(organizations.find(org => org.id === e.value))
                setActiveIndex(null) // Reset active index when organization changes
              }}
              optionLabel="name"
              optionValue="id"
              placeholder="No organizations available"
            />
          </div>
        )}
      </div>
      <Accordion
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        {blocks.map((block) => {
          return (
            <AccordionTab
              key={block.block_name}
              header={
                <div className="pt-2 pb-2">
                  {block.block_title}
                </div>
              }
              id={block.block_name}
            >
              <Form
                key={block.block_name}
                blockName={block.block_name}
                className={block.className}
                organization_id={selectedOrganization?.id}
                organization_alias={selectedOrganization?.alias}
                enterprise_id={selectedOrganization?.enterprise_id}
                enterprise_alias={selectedOrganization?.enterprise_alias}
              />
            </AccordionTab>
          )
        })}
      </Accordion>
    </div>
  )
}

export default ProfilePage
